import { computed, type MaybeRefOrGetter, toValue } from "vue";

import { useFilters } from "@/composables/useFilters";
import { TravelOrderPaymentStatus } from "@/constants/travelOrder";

import type { OrderExpense, OrderPayment } from "@/api/orders/dto/order.dto";
import type { TravelOrderDto } from "@/api/trips/dto/trip.dto";

export function useCalculateOrderMoney(
  orderPayments: MaybeRefOrGetter<OrderPayment[]>,
  orderExpenses: MaybeRefOrGetter<OrderExpense[]>,
  travelOrders: MaybeRefOrGetter<TravelOrderDto[]>,
) {
  const { round } = useFilters();

  const hasTravelOrders = computed<boolean>(() => toValue(travelOrders).length > 0);

  const totalInvoiceRate = computed<number>(() => {
    return round(
      toValue(orderPayments)
        .filter((payment) => payment.isActive !== false && !payment.isDeleted)
        .reduce((total, payment) => total + (payment.rate ?? 0), 0),
    );
  });

  const totalExpenses = computed<number>(() => {
    return round(toValue(orderExpenses).reduce((total, expense) => total + expense.rate, 0));
  });

  const dispatcherExpenses = computed<number>(() => {
    return round(
      toValue(orderExpenses)
        .filter((expense) => expense.affectsDispatcherProfit)
        .reduce((total, expense) => total + expense.rate, 0),
    );
  });

  const totalDriverRate = computed<number>(() => {
    return round(
      toValue(travelOrders)
        .flatMap((travelOrder) => travelOrder.payments)
        .filter((payment) => payment.status !== TravelOrderPaymentStatus.Canceled)
        .reduce((total, payment) => total + Number(payment.amount ?? 0), 0),
    );
  });

  const totalOrderRate = computed<number>(() => round(totalInvoiceRate.value - totalExpenses.value));

  const companyProfit = computed<number>(() => {
    return round(totalInvoiceRate.value - totalDriverRate.value - totalExpenses.value);
  });

  const dispatcherProfit = computed<number>(() => {
    return round(totalInvoiceRate.value - totalDriverRate.value - dispatcherExpenses.value);
  });

  return {
    hasTravelOrders,
    totalInvoiceRate,
    totalExpenses,
    totalOrderRate,
    totalDriverRate,
    companyProfit,
    dispatcherProfit,
  };
}
